import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/common/Breadcrumb';
import ReactDataTable from 'react-data-table-component';
import { IconChevronLeft, IconAlertCircle, IconLoader2 } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import { useHrDashboard } from '../../hooks/useDashboard';
import { PageLoader, TableLoader } from '../../components/common/Loader';

const AVATAR_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4'];

function EmployeeCell({ name, sub }) {
    const initials = name ? name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';
    const bg = AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
    return (
        <div className="flex items-center gap-10 py-5">
            <div
                style={{ backgroundColor: bg }}
                className="w-[36px] h-[36px] rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0"
            >
                {initials}
            </div>
            <div>
                <p className="font-medium text-sm leading-none mb-2">{name}</p>
                {sub && <p className="text-[11px] text-text-secondary">{sub}</p>}
            </div>
        </div>
    );
}

const TYPE_CONFIG = {
    'active-employees': {
        title: 'Active Employees',
        description: 'All currently active employees in the organisation.',
        badge: 'bg-success/10 text-success',
        getData: (data) => data?.workforce?.employees || [],
        columns: [
            {
                name: 'Employee',
                selector: row => row.employee_name || row.name,
                sortable: true,
                minWidth: '220px',
                cell: row => <EmployeeCell name={row.employee_name || row.name} sub={row.employee_no || row.email} />,
            },
            { name: 'Role / Title', selector: row => row.job_title || row.role || '—', sortable: true },
            { name: 'Department', selector: row => row.department || '—', sortable: true },
        ],
    },
    'open-timesheets': {
        title: 'Open Timesheets',
        description: 'Employees with timesheets currently open and pending submission.',
        badge: 'bg-warning/10 text-warning',
        getData: (data) => data?.timesheet?.open?.employees || [],
        columns: [
            {
                name: 'Employee',
                selector: row => row.employee_name,
                sortable: true,
                minWidth: '220px',
                cell: row => <EmployeeCell name={row.employee_name} sub={row.email} />,
            },
            { name: 'Department', selector: row => row.department || '—', sortable: true },
            { name: 'Line Manager', selector: row => row.line_manager || '—', sortable: true },
            { name: 'Period', selector: row => row.period || '—', sortable: true },
        ],
    },
    'overdue-timesheets': {
        title: 'Overdue Timesheet Submissions',
        description: 'Employees who have missed their timesheet submission deadline.',
        badge: 'bg-danger/10 text-danger',
        getData: (data) => data?.timesheet?.overdue_submission?.employees || [],
        columns: [
            {
                name: 'Employee',
                selector: row => row.employee_name,
                sortable: true,
                minWidth: '220px',
                cell: row => <EmployeeCell name={row.employee_name} sub={row.email} />,
            },
            { name: 'Department', selector: row => row.department || '—', sortable: true },
            { name: 'Line Manager', selector: row => row.line_manager || '—', sortable: true },
            {
                name: 'Reason',
                selector: row => row.reason,
                cell: row => (
                    <span className="px-8 py-3 rounded-full text-[10px] font-medium bg-danger/10 text-danger uppercase">
                        {row.reason || 'Overdue'}
                    </span>
                ),
            },
        ],
    },
    'timesheet-approved': {
        title: 'Approved Timesheets',
        description: 'Timesheets that have been reviewed and approved.',
        badge: 'bg-success/10 text-success',
        getData: (data) => data?.timesheet?.approved?.employees || [],
        columns: [
            {
                name: 'Employee',
                selector: row => row.employee_name,
                sortable: true,
                minWidth: '220px',
                cell: row => <EmployeeCell name={row.employee_name} sub={row.email} />,
            },
            { name: 'Department', selector: row => row.department || '—', sortable: true },
            { name: 'Line Manager', selector: row => row.line_manager || '—', sortable: true },
            { name: 'Period', selector: row => row.period || '—', sortable: true },
            {
                name: 'Status',
                selector: row => row.status,
                cell: () => (
                    <span className="px-8 py-3 rounded-full text-[10px] font-medium bg-success/10 text-success uppercase">
                        Approved
                    </span>
                ),
            },
        ],
    },
    'kpi-submitted': {
        title: 'KPIs Submitted',
        description: 'Employees who have submitted their KPI assessments.',
        badge: 'bg-primary/10 text-primary',
        getData: (data) => data?.kpi?.submitted?.employees || [],
        columns: [
            {
                name: 'Employee',
                selector: row => row.employee_name || row.name,
                sortable: true,
                minWidth: '220px',
                cell: row => <EmployeeCell name={row.employee_name || row.name} sub={row.employee_no || row.employeeId} />,
            },
            { name: 'Department', selector: row => row.department || '—', sortable: true },
            {
                name: 'Status',
                selector: row => row.status,
                cell: () => (
                    <span className="px-8 py-3 rounded-full text-[10px] font-medium bg-primary/10 text-primary uppercase">
                        Submitted
                    </span>
                ),
            },
        ],
    },
    'leave-pending': {
        title: 'Pending Leave Requests',
        description: 'Leave requests awaiting approval.',
        badge: 'bg-warning/10 text-warning',
        getData: (data) => data?.leave?.pending?.employees || [],
        columns: [
            {
                name: 'Employee',
                selector: row => row.employee_name || row.name,
                sortable: true,
                minWidth: '200px',
                cell: row => <EmployeeCell name={row.employee_name || row.name} sub={row.employee_no} />,
            },
            { name: 'Leave Type', selector: row => row.leave_type || '—', sortable: true },
            { name: 'From', selector: row => row.start_date || '—', sortable: true },
            { name: 'To', selector: row => row.end_date || '—', sortable: true },
            { name: 'Days', selector: row => row.days_requested || '—', sortable: true, width: '80px' },
            {
                name: 'Status',
                cell: () => (
                    <span className="px-8 py-3 rounded-full text-[10px] font-medium bg-warning/10 text-warning uppercase">
                        Pending
                    </span>
                ),
            },
        ],
    },
    'leave-approved': {
        title: 'Approved Leave Requests',
        description: 'Leave requests that have been approved.',
        badge: 'bg-success/10 text-success',
        getData: (data) => data?.leave?.approved?.employees || [],
        columns: [
            {
                name: 'Employee',
                selector: row => row.employee_name || row.name,
                sortable: true,
                minWidth: '200px',
                cell: row => <EmployeeCell name={row.employee_name || row.name} sub={row.employee_no} />,
            },
            { name: 'Leave Type', selector: row => row.leave_type || '—', sortable: true },
            { name: 'From', selector: row => row.start_date || '—', sortable: true },
            { name: 'To', selector: row => row.end_date || '—', sortable: true },
            { name: 'Days', selector: row => row.days_requested || '—', sortable: true, width: '80px' },
            {
                name: 'Status',
                cell: () => (
                    <span className="px-8 py-3 rounded-full text-[10px] font-medium bg-success/10 text-success uppercase">
                        Approved
                    </span>
                ),
            },
        ],
    },
    'leave-rejected': {
        title: 'Rejected Leave Requests',
        description: 'Leave requests that have been rejected.',
        badge: 'bg-danger/10 text-danger',
        getData: (data) => data?.leave?.rejected?.employees || [],
        columns: [
            {
                name: 'Employee',
                selector: row => row.employee_name || row.name,
                sortable: true,
                minWidth: '200px',
                cell: row => <EmployeeCell name={row.employee_name || row.name} sub={row.employee_no} />,
            },
            { name: 'Leave Type', selector: row => row.leave_type || '—', sortable: true },
            { name: 'From', selector: row => row.start_date || '—', sortable: true },
            { name: 'To', selector: row => row.end_date || '—', sortable: true },
            { name: 'Days', selector: row => row.days_requested || '—', sortable: true, width: '80px' },
            {
                name: 'Status',
                cell: () => (
                    <span className="px-8 py-3 rounded-full text-[10px] font-medium bg-danger/10 text-danger uppercase">
                        Rejected
                    </span>
                ),
            },
        ],
    },
};

export default function DashboardDetail() {
    const { type } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const orgId = user?.orgId;
    const { data, isLoading, isError } = useHrDashboard(orgId);
    const [filterText, setFilterText] = useState('');

    const config = TYPE_CONFIG[type];
    const listData = config ? (config.getData(data) || []) : [];

    const filteredData = useMemo(() => {
        if (!filterText.trim()) return listData;
        const q = filterText.toLowerCase();
        return listData.filter(row => {
            const name = (row.employee_name || row.name || '').toLowerCase();
            const dept = (row.department || '').toLowerCase();
            const email = (row.email || '').toLowerCase();
            return name.includes(q) || dept.includes(q) || email.includes(q);
        });
    }, [listData, filterText]);

    if (!config) return (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-15">
            <div className="w-60 h-60 rounded-full bg-danger/10 flex items-center justify-center">
                <IconAlertCircle size={28} className="text-danger" />
            </div>
            <div className="text-center">
                <p className="font-semibold mb-5">Invalid detail type</p>
                <p className="text-text-secondary text-sm">The category you requested does not exist.</p>
                <button onClick={() => navigate('/dashboard')} className="btn btn-primary mt-15">Back to Dashboard</button>
            </div>
        </div>
    );

    if (isLoading) return (
        <PageLoader minHeight="400px" text="Loading..." />
    );

    if (isError) return (
        <div className="flex items-center gap-10 p-20 bg-danger/10 border border-danger/30 rounded-lg text-danger">
            <IconAlertCircle size={20} className="flex-shrink-0" />
            <span className="text-sm">Failed to load data. Please try again.</span>
        </div>
    );

    return (
        <>
            <Breadcrumb title={config.title} path={`Dashboard / ${config.title}`} />

            <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                {/* Back button */}
                <button
                    onClick={() => navigate('/dashboard')}
                    className="btn btn-outline-secondary flex items-center gap-5 mb-15"
                >
                    <IconChevronLeft size={16} />
                    Back
                </button>

                {/* Header */}
                <div className="flex items-start justify-between gap-15 flex-wrap mb-20">
                    <div className="flex items-center gap-15">
                        <div>
                            <div className="flex items-center gap-10 flex-wrap">
                                <h5 className="text-[16px]/[20px] font-semibold">{config.title}</h5>
                                <span className={`px-10 py-3 rounded-full text-xs font-medium ${config.badge}`}>
                                    {listData.length} record{listData.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <p className="text-text-secondary text-sm mt-3">{config.description}</p>
                        </div>
                    </div>
                    <div className="relative w-full max-w-[221px]">
                        <input
                            type="text"
                            value={filterText}
                            onChange={e => setFilterText(e.target.value)}
                            placeholder="Search here..."
                            className="bg-bg-light border border-border w-full py-[6px] px-12 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none text-sm"
                        />
                    </div>
                </div>

                <div className="react-data-table">
                    <ReactDataTable
                        columns={config.columns}
                        data={filteredData}
                        pagination
                        highlightOnHover
                        onRowClicked={type === 'active-employees' ? (row) => navigate(`/employee/${row.id}/snapshot`) : undefined}
                        pointerOnHover={type === 'active-employees'}
                        noDataComponent={
                            <div className="py-40 text-center">
                                <p className="text-text-secondary text-sm">
                                    {filterText ? 'No results match your search.' : 'No records found for this category.'}
                                </p>
                            </div>
                        }
                    />
                </div>
            </div>
        </>
    );
}
